import { Box, Button, Flex, Menu, Spinner, Text } from '@chakra-ui/react';
import { CaretDown } from '@phosphor-icons/react';
import { Controller, Control, FieldPath, FieldValues } from 'react-hook-form';

import { Field } from '@/components/ui/field';

const ErrorMessage = ({ message }: { message?: string }) =>
  message ? (
    <Box fontSize="14px" color="error.700" role="alert">
      {message}
    </Box>
  ) : null;

interface DropdownFieldProps<T, TFieldValues extends FieldValues = FieldValues> {
  name: FieldPath<TFieldValues>;
  label: React.ReactNode;
  control: Control<TFieldValues>;
  rules?: Record<string, unknown>;
  error?: { message?: string };
  disabled?: boolean;
  placeholder?: string;
  items: T[];
  getItemValue: (item: T) => string;
  getItemLabel: (item: T) => string;
  onSelectionChange?: (item: T | undefined) => void;
  selectedValue?: string;
  isLoading?: boolean;
  loadingText?: string;
  width?: string;
}

export function DropdownField<T, TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  control,
  rules,
  error,
  disabled = false,
  placeholder = 'Select option',
  items = [],
  getItemValue,
  getItemLabel,
  onSelectionChange,
  selectedValue,
  isLoading = false,
  loadingText = 'Loading...',
  width = '100%'
}: DropdownFieldProps<T, TFieldValues>) {
  return (
    <Field label={label} invalid={!!error} errorText={<ErrorMessage message={error?.message} />}>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => (
          <Menu.Root>
            <Menu.Trigger asChild>
              <Button
                variant="outline"
                size="lg"
                borderColor={error ? 'error.500' : 'neutrals.200'}
                borderRadius="8px"
                disabled={disabled}
                _disabled={{ bg: 'neutrals.300' }}
                w={width}
                aria-label={placeholder}
              >
                <Flex justifyContent="space-between" alignItems="center" w="100%">
                  <Box
                    fontSize="16px"
                    color="neutrals.700"
                    overflow="hidden"
                    whiteSpace="nowrap"
                    textOverflow="ellipsis"
                    display="flex"
                    alignItems="center"
                  >
                    {isLoading ? (
                      <Flex gap="12px" alignContent="center">
                        <Spinner w="16px" h="16px" mt="4px" />
                        <Text fontSize="16px" mr="8px">
                          {loadingText}
                        </Text>
                      </Flex>
                    ) : field.value ? (
                      getItemLabel(field.value?.value)
                    ) : (
                      placeholder
                    )}
                  </Box>
                  <Box as="span" ml="8px" mr="0">
                    <CaretDown size={16} aria-hidden="true" />
                  </Box>
                </Flex>
              </Button>
            </Menu.Trigger>
            <Menu.Positioner>
              <Menu.Content
                minW="20rem"
                position="fixed"
                zIndex={1500}
                maxH="300px"
                overflowY="auto"
              >
                <Menu.RadioItemGroup
                  value={selectedValue || ''}
                  onValueChange={(value) => {
                    const selectedItem = items?.find((item) => getItemValue(item) === value.value);
                    field.onChange({ value: selectedItem });
                    onSelectionChange?.(selectedItem);
                  }}
                >
                  {items?.map((item) => (
                    <Menu.RadioItem
                      key={getItemValue(item)}
                      value={getItemValue(item)}
                      fontSize="16px"
                      _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                    >
                      {getItemLabel(item)}
                    </Menu.RadioItem>
                  ))}
                </Menu.RadioItemGroup>
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>
        )}
      />
    </Field>
  );
}
